import moment from 'moment-timezone';

/**
 * Filtros de la pantalla Facturación y estadísticas. La misma traducción alimenta el cuerpo de
 * `POST /api/listados/facturacion/enviar` y el filtrado de la tabla, para que el listado que llega
 * por email sea el que se ve en pantalla. El filtrado replica el del backend
 * (`helpers/listadoFacturacion.ts`): la sucursal y el profesional los resuelve la consulta de
 * ventas, y aquí se aplican el periodo (sobre `fechaVenta`, como la consulta), la franja horaria,
 * los filtros de cabecera y el tipo de venta.
 */

export const SIN_SUCURSAL = 'SIN_SUCURSAL';

export const TIPO_VENTA_TODOS = 'todos';
export const TIPO_VENTA_SERVICIOS = 'servicios';
export const TIPO_VENTA_PRODUCTOS = 'productos';
export const TIPO_VENTA_MIXTAS = 'mixtas';

const FORMATO_HORA = 'HH:mm';

const idsDe = (entidades) => entidades.map((entidad) => entidad._id);

// Un `<input type="time">` vacío deja la cadena vacía, que el backend rechazaría como hora: el
// extremo vacío se omite y significa "sin límite por ese lado".
const franjaHoraria = ({ start: horaInicio, end: horaFin }) => ({
    ...(horaInicio ? { horaInicio } : {}),
    ...(horaFin ? { horaFin } : {}),
});

/**
 * Traduce el estado de la pantalla al cuerpo que espera el envío del listado de facturación.
 *
 * @param {object} estadoPantalla
 * @param {{ start, end }} estadoPantalla.dateRange - fechas (moment o Date) del periodo.
 * @param {{ start: string, end: string }} estadoPantalla.timeRange - jornada `HH:mm`.
 * @param {Array<{ _id }>} estadoPantalla.selectedSucursal - incluye `{ _id: SIN_SUCURSAL }`.
 * @param {Array<{ _id }>} estadoPantalla.selectedProfesional
 * @param {object} estadoPantalla.selectedFilters - nombres elegidos en las cabeceras de la tabla.
 * @param {string} estadoPantalla.filtroTipoVenta
 */
export const construirFiltrosListadoFacturacion = (estadoPantalla) => {
    const {
        dateRange,
        timeRange,
        selectedSucursal,
        selectedProfesional,
        selectedFilters,
        filtroTipoVenta,
    } = estadoPantalla;

    return {
        fechaInicio: moment(dateRange.start).toISOString(),
        fechaFin: moment(dateRange.end).toISOString(),
        sucursales: idsDe(selectedSucursal),
        profesionales: idsDe(selectedProfesional),
        ...franjaHoraria(timeRange),
        servicios: selectedFilters.servicio,
        variantes: selectedFilters.variante,
        productos: selectedFilters.producto,
        descuentos: selectedFilters.descuento,
        metodosPago: selectedFilters.metodoPago,
        tipoVenta: filtroTipoVenta,
    };
};

// La consulta ya acota por `fechaVenta` (`$gte`/`$lte`). Se repite aquí para las filas que la
// pantalla añade sin volver a consultar: la anulación de una venta lleva `fechaVenta` de ahora y,
// con un periodo pasado, ni la consulta ni el listado la incluirían.
const estaEnPeriodo = (venta, { fechaInicio, fechaFin }) => {
    if (!venta.fechaVenta) return false;
    return moment(venta.fechaVenta).isBetween(fechaInicio, fechaFin, undefined, '[]');
};

// Cada extremo se aplica por separado y ambos son inclusivos.
const estaEnFranjaHoraria = (venta, { horaInicio, horaFin }) => {
    const horaCita = moment(venta.fechaCita).format(FORMATO_HORA);
    if (horaInicio && horaCita < horaInicio) return false;
    if (horaFin && horaCita > horaFin) return false;
    return true;
};

const coincideAlguno = (valoresVenta, seleccionados) => {
    const sinFiltro = !seleccionados || seleccionados.length === 0;
    if (sinFiltro) return true;
    return valoresVenta.some((valor) => seleccionados.includes(valor));
};

const nombresDe = (lineas) => lineas.map((linea) => linea?.nombre);

const titulosDeProductos = (lineasProducto) =>
    lineasProducto.map((lineaProducto) => lineaProducto?.titulo || lineaProducto?.nombre);

const titulosDeDescuentos = (venta) => (venta.promociones || []).map((promocion) => promocion?.titulo);

const cumpleFiltrosCabecera = (venta, filtrosListado, obtenerLineasVenta) => {
    const { serviciosMostrar, variantesMostrar, productosMostrar } = obtenerLineasVenta(venta);
    return coincideAlguno(nombresDe(serviciosMostrar), filtrosListado.servicios)
        && coincideAlguno(titulosDeDescuentos(venta), filtrosListado.descuentos)
        && coincideAlguno(nombresDe(variantesMostrar), filtrosListado.variantes)
        && coincideAlguno([venta.metodoPago], filtrosListado.metodosPago)
        && coincideAlguno(titulosDeProductos(productosMostrar), filtrosListado.productos);
};

// "Solo servicios" exige productos exactamente a 0, no <= 0, igual que el backend.
const cumpleTipoVenta = (venta, tipoVenta) => {
    const importeServicios = venta.importeServicios || 0;
    const importeProductos = venta.importeProductos || 0;
    if (tipoVenta === TIPO_VENTA_SERVICIOS) return importeServicios > 0 && importeProductos === 0;
    if (tipoVenta === TIPO_VENTA_PRODUCTOS) return importeProductos > 0 && importeServicios === 0;
    if (tipoVenta === TIPO_VENTA_MIXTAS) return importeServicios > 0 && importeProductos > 0;
    return true;
};

/**
 * Ventas que se muestran en la tabla, a partir de las que ya devolvió la consulta.
 *
 * @param {Array} ventas - ventas facturables que devolvió la consulta del periodo, sucursal y
 *   profesional elegidos, más las filas de anulación añadidas en pantalla.
 * @param {object} filtrosListado - salida de `construirFiltrosListadoFacturacion`.
 * @param {Function} obtenerLineasVenta - resuelve las líneas de una venta contra el catálogo y
 *   devuelve `{ serviciosMostrar, variantesMostrar, productosMostrar }`.
 */
export const filtrarVentasTablaFacturacion = (ventas, filtrosListado, obtenerLineasVenta) =>
    ventas.filter((venta) =>
        estaEnPeriodo(venta, filtrosListado)
        && estaEnFranjaHoraria(venta, filtrosListado)
        && cumpleFiltrosCabecera(venta, filtrosListado, obtenerLineasVenta)
        && cumpleTipoVenta(venta, filtrosListado.tipoVenta)
    );
